import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import { privateClient } from "@/api/privateClient";
import { defaultOrgTheme } from "../defaultOrgTheme";

interface OrgTheme {
  primary: string;
  primaryForeground: string;
  secondary: string;
  accent: string;
}

interface ThemeState {
  orgTheme: OrgTheme;
  loading: boolean;
}

const initialState: ThemeState = {
  orgTheme: defaultOrgTheme,
  loading: false,
};

// Only accept "H S% L%" HSL-triplet strings (e.g. "29 100% 50%"). Anything else
// falls back to the platform default so a malformed/malicious API response can
// never be written into CSS custom properties.
const HSL_TRIPLET = /^\d{1,3}\s+\d{1,3}%\s+\d{1,3}%$/;

function sanitizeOrgTheme(raw: unknown): OrgTheme {
  const candidate = (raw && typeof raw === "object" ? raw : {}) as Partial<OrgTheme>;
  const pick = (key: keyof OrgTheme) =>
    typeof candidate[key] === "string" && HSL_TRIPLET.test(candidate[key] as string)
      ? (candidate[key] as string)
      : defaultOrgTheme[key];

  return {
    primary: pick("primary"),
    primaryForeground: pick("primaryForeground"),
    secondary: pick("secondary"),
    accent: pick("accent"),
  };
}

// Fetch the logged-in org's saved branding colors from backend.
export const fetchOrgTheme = createAsyncThunk(
  "theme/fetchOrgTheme",
  async (organizationId: number) => {
    const { data } = await privateClient.get(`/organizations/${organizationId}/theme`);
    return sanitizeOrgTheme(data);
  }
);

const themeSlice = createSlice({
  name: "theme",
  initialState,
  reducers: {
    setOrgTheme: (state, action: PayloadAction<OrgTheme>) => {
      state.orgTheme = sanitizeOrgTheme(action.payload);
    },
    resetTheme: (state) => {
      state.orgTheme = defaultOrgTheme;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrgTheme.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchOrgTheme.fulfilled, (state, action) => {
        state.loading = false;
        state.orgTheme = action.payload;
      })
      .addCase(fetchOrgTheme.rejected, (state) => {
        state.loading = false;
        state.orgTheme = defaultOrgTheme; 
      });
  },
});

export const { setOrgTheme, resetTheme } = themeSlice.actions;
export default themeSlice.reducer;